import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useCRM } from '../../utils/CRMContext';
import { EnhancedDeal, Pipeline, LeadSource, FrameType, ProposalBuilderData, KitProposal } from '../../types';
import { ActivityQuickLog } from '../activities/ActivityQuickLog';
import { getSourceColor } from '../../utils/sourceColors';
import { formatSourceName } from '../../utils/sourceUtils';
import {
  getProjectDisplayName,
  defaultClientLabelFromLead,
  defaultDescriptorFromProjectType,
  isLegacyDealName,
  parseLegacyTitle,
} from '../../utils/projectName';
import { NameProjectWizard } from '../projects/NameProjectWizard';
import CompanyBillingAddressFields from '../projects/CompanyBillingAddressFields';
import BiddingModal from '../bidding/BiddingModal';
import BidDetailsPanel from '../bidding/BidDetailsPanel';
import { Bid } from '../../types/bidding';
import EngagementLettersList from '../engagement/EngagementLettersList';
import KitSelectorModal from '../kits/KitSelectorModal';
import { AlertTriangle, Trash2, PartyPopper, Home, Package, FileText, RotateCcw, FileCheck, Download, Loader2, LayoutDashboard, Search, Sparkles, Crosshair, ArrowRightLeft } from 'lucide-react';
import {
  getAssignedDesigner,
  getExternalArchitectName,
  listDesigners,
} from '../../utils/dealDesignerArchitect';
import { getAssignedArchitectUids, listAssignableArchitects } from '../../utils/architectProjectAccess';
import { TransferDealToLeadModal } from './TransferDealToLeadModal';
import { useNavigate } from 'react-router-dom';
import { db } from '../../utils/firebase';
import { doc, getDoc, deleteDoc, updateDoc, deleteField, Timestamp, type DocumentData, type UpdateData } from 'firebase/firestore';
import { LeadIntelligencePanel } from './LeadIntelligencePanel';
import AIAdvisorPanel from '../leads/AIAdvisorPanel';
import './DealDetailsPanel.css';

interface DealDetailsPanelProps {
  deal: EnhancedDeal;
  pipeline: Pipeline;
  onClose: () => void;
  userId: string;
}

type TabType = 'details' | 'intel' | 'activities' | 'history';

export const DealDetailsPanel: React.FC<DealDetailsPanelProps> = ({
  deal,
  pipeline,
  onClose,
  userId,
}) => {
  const navigate = useNavigate();
  const {
    updateEnhancedDeal,
    markDealWon,
    markDealLost,
    markDealActive,
    deleteEnhancedDeal,
    convertDealToProject,
    leads,
    updateLead,
    getBidsForDeal,
    crmUsers,
    architectInvites,
    linkDealToLead,
  } = useCRM();

  // Get linked lead
  const linkedLead = useMemo(() =>
    deal.leadId ? leads.find(l => l.id === deal.leadId) : undefined,
    [deal.leadId, leads]
  );

  // Check if proposal exists for this deal and get proposal data
  const [proposalData, setProposalData] = useState<ProposalBuilderData | null>(null);
  const [showTransferDealModal, setShowTransferDealModal] = useState(false);

  useEffect(() => {
    const checkProposal = async () => {
      if (!deal.id) return;
      try {
        const proposalRef = doc(db, 'proposalBuilders', deal.id);
        const snapshot = await getDoc(proposalRef);
        if (snapshot.exists()) {
          setHasExistingProposal(true);
          setProposalData({ id: snapshot.id, ...snapshot.data() } as ProposalBuilderData);
        } else {
          setHasExistingProposal(false);
          setProposalData(null);
        }
      } catch (error) {
        console.error('Error checking proposal:', error);
      }
    };
    checkProposal();
  }, [deal.id]);

  // Reset proposal handler
  const handleResetProposal = async () => {
    if (!deal.id) return;
    setResettingProposal(true);
    try {
      const proposalRef = doc(db, 'proposalBuilders', deal.id);
      await deleteDoc(proposalRef);
      setHasExistingProposal(false);
      setShowResetProposalModal(false);
      alert('Proposal has been reset. You can now start fresh.');
    } catch (error) {
      console.error('Error resetting proposal:', error);
      alert('Failed to reset proposal. Please try again.');
    } finally {
      setResettingProposal(false);
    }
  };

  // Download signed PDF - refresh data and open URL
  const handleDownloadSignedPdf = async () => {
    if (!deal.id || !proposalData?.signature) return;

    setGeneratingSignedPdf(true);
    try {
      // Refresh data to get latest signedPdfUrl
      const proposalRef = doc(db, 'proposalBuilders', deal.id);
      const snapshot = await getDoc(proposalRef);

      if (snapshot.exists()) {
        const data = { id: snapshot.id, ...snapshot.data() } as ProposalBuilderData;
        setProposalData(data);

        if (data.signedPdfUrl) {
          // URL exists, open it
          window.open(data.signedPdfUrl, '_blank');
        } else if (data.slug) {
          // No URL yet - open public proposal page where client can download
          const publicUrl = `${window.location.origin}/proposal/${data.slug}`;
          window.open(publicUrl, '_blank');
        } else {
          alert('PDF not available. The proposal may need to be accessed from the client page first.');
        }
      }
    } catch (error) {
      console.error('Error fetching signed PDF:', error);
      alert('Failed to fetch PDF. Please try again.');
    } finally {
      setGeneratingSignedPdf(false);
    }
  };

  // Get source color - prefer linked lead's source, then deal's source
  const sourceColor = useMemo(() => {
    if (linkedLead?.source) return getSourceColor(linkedLead.source);
    if (deal.source) return getSourceColor(deal.source);
    return '#9AA0A6'; // Gray fallback
  }, [linkedLead?.source, deal.source]);

  // Source letter for badge
  const sourceLetter = useMemo(() => {
    const src = linkedLead?.source || deal.source;
    if (src && src !== 'unspecified' && src.trim()) {
      return src.trim()[0].toUpperCase();
    }
    return '?';
  }, [linkedLead?.source, deal.source]);

  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [intelSubTab, setIntelSubTab] = useState<'enrich' | 'insights'>('enrich');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [showLostModal, setShowLostModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showBiddingModal, setShowBiddingModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState<Bid | null>(null);
  const [selectedFrameType, setSelectedFrameType] = useState<FrameType>('custom');
  const [lostReason, setLostReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetProposalModal, setShowResetProposalModal] = useState(false);
  const [hasExistingProposal, setHasExistingProposal] = useState(false);
  const [resettingProposal, setResettingProposal] = useState(false);
  const [generatingSignedPdf, setGeneratingSignedPdf] = useState(false);
  const [showRenameWizard, setShowRenameWizard] = useState(false);

  // Kit selection state
  const [showKitSelector, setShowKitSelector] = useState(false);
  const [kitProposal, setKitProposal] = useState<KitProposal | null>(null);

  // Check if pipeline is "Timber Kits"
  const isTimberKitsPipeline = pipeline?.name?.toLowerCase().includes('timber kit') ||
    pipeline?.name?.toLowerCase().includes('kit');

  const linkedLeadDisplayName = useMemo(() => {
    if (!linkedLead) return '';
    return `${linkedLead.firstName || ''} ${linkedLead.lastName || ''}`.trim();
  }, [linkedLead]);

  const dealForProjectDisplayName = useMemo(() => {
    const clientLabel = linkedLead
      ? defaultClientLabelFromLead(linkedLead)
      : deal.clientLabel;
    return {
      ...deal,
      title: deal.title,
      clientLabel,
      contactName: linkedLeadDisplayName || deal.contactName,
      company: linkedLead?.company || deal.company,
    };
  }, [deal, linkedLead, linkedLeadDisplayName]);

  const displayContactName = linkedLeadDisplayName || deal.contactName || '';

  // Local values for inline editing
  const [localValues, setLocalValues] = useState({
    title: deal.title,
    value: deal.value,
    description: deal.description || '',
    expectedCloseDate: deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toISOString().split('T')[0] : '',
    source: linkedLead?.source || deal.source || 'unspecified' as LeadSource,
    location: linkedLead?.location || deal.location || '',
    projectType: linkedLead?.projectType || deal.projectType || '',
    timeline: linkedLead?.timeline || deal.timeline || '',
    company: linkedLead?.company || deal.company || '',
    designPaymentDate: deal.designPaymentDate ? (deal.designPaymentDate instanceof Date ? deal.designPaymentDate.toISOString().split('T')[0] : (deal.designPaymentDate as any)?.toDate?.()?.toISOString().split('T')[0] || '') : '',
    createdAt: deal.createdAt ? (deal.createdAt instanceof Date ? deal.createdAt.toISOString().split('T')[0] : '') : '',
    closedAt: deal.closedAt ? (deal.closedAt instanceof Date ? deal.closedAt.toISOString().split('T')[0] : (deal.closedAt as any)?.toDate?.()?.toISOString().split('T')[0] || '') : '',
    currentStageEnteredAt: deal.currentStageEnteredAt ? (deal.currentStageEnteredAt instanceof Date ? deal.currentStageEnteredAt.toISOString().split('T')[0] : '') : '',
  });

  useEffect(() => {
    setLocalValues(prev => ({
      ...prev,
      title: deal.title,
      value: deal.value,
      description: deal.description || '',
      source: linkedLead?.source || deal.source || 'unspecified',
      location: linkedLead?.location || deal.location || '',
      projectType: linkedLead?.projectType || deal.projectType || '',
      timeline: linkedLead?.timeline || deal.timeline || '',
      company: linkedLead?.company || deal.company || '',
    }));
  }, [
    deal.id,
    deal.title,
    deal.value,
    deal.description,
    deal.source,
    deal.location,
    deal.projectType,
    deal.timeline,
    deal.company,
    linkedLead?.id,
    linkedLead?.source,
    linkedLead?.location,
    linkedLead?.projectType,
    linkedLead?.timeline,
    linkedLead?.company,
    linkedLead?.firstName,
    linkedLead?.lastName,
  ]);

  const displayProjectTitle = useMemo(() => {
    if (linkedLead && isLegacyDealName(deal) && linkedLeadDisplayName) {
      const projName = deal.title?.includes(' - ')
        ? deal.title.split(' - ').slice(1).join(' - ')
        : (deal.descriptor || '').trim() || deal.title || '';
      return projName ? `${linkedLeadDisplayName} - ${projName}` : linkedLeadDisplayName;
    }
    return getProjectDisplayName({ ...dealForProjectDisplayName, title: localValues.title });
  }, [deal, linkedLead, linkedLeadDisplayName, dealForProjectDisplayName, localValues.title]);

  // Update local source color when source changes
  const currentSourceColor = useMemo(() => {
    return getSourceColor(localValues.source);
  }, [localValues.source]);

  // Handle field update - updates local state and saves to database immediately
  const handleFieldUpdate = useCallback(async (field: string, value: any) => {
    // Update local state immediately
    setLocalValues(prev => ({ ...prev, [field]: value }));

    if (!deal.id) return;

    try {
      // Prepare deal updates
      const dealUpdates: Partial<EnhancedDeal> = {};

      switch (field) {
        case 'title':
          dealUpdates.title = value;
          break;
        case 'value':
          dealUpdates.value = parseFloat(value) || 0;
          break;
        case 'description':
          dealUpdates.description = value || undefined;
          break;
        case 'expectedCloseDate':
          dealUpdates.expectedCloseDate = value ? new Date(value) : undefined;
          break;
        case 'source':
          dealUpdates.source = value;
          dealUpdates.colorBar = getSourceColor(value);
          // Also update the linked lead
          if (linkedLead?.id) {
            await updateLead(linkedLead.id, { source: value });
          }
          break;
        case 'location':
          dealUpdates.location = value;
          if (linkedLead?.id) {
            await updateLead(linkedLead.id, { location: value });
          }
          break;
        case 'projectType':
          dealUpdates.projectType = value;
          if (linkedLead?.id) {
            await updateLead(linkedLead.id, { projectType: value });
          }
          break;
        case 'timeline':
          dealUpdates.timeline = value || undefined;
          if (linkedLead?.id) {
            await updateLead(linkedLead.id, { timeline: value || undefined });
          }
          break;
        case 'company':
          dealUpdates.company = value || undefined;
          if (linkedLead?.id) {
            await updateLead(linkedLead.id, { company: value || '' });
          }
          break;
        case 'designPaymentDate':
          (dealUpdates as any).designPaymentDate = value ? new Date(value + 'T12:00:00') : undefined;
          break;
        case 'createdAt':
          (dealUpdates as any).createdAt = value ? new Date(value + 'T12:00:00') : deal.createdAt;
          break;
        case 'closedAt':
          dealUpdates.closedAt = value ? new Date(value + 'T12:00:00') : undefined;
          break;
        case 'currentStageEnteredAt':
          (dealUpdates as any).currentStageEnteredAt = value ? new Date(value + 'T12:00:00') : deal.currentStageEnteredAt;
          break;
      }

      // Save to database
      await updateEnhancedDeal(deal.id, dealUpdates);
    } catch (error) {
      console.error('Error updating deal field:', error);
      // Revert on error
      setLocalValues(prev => ({
        ...prev,
        [field]: field === 'title' ? deal.title :
          field === 'value' ? deal.value :
            field === 'description' ? (deal.description || '') :
              field === 'expectedCloseDate' ? (deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toISOString().split('T')[0] : '') :
                field === 'source' ? (linkedLead?.source || deal.source || 'unspecified') :
                  field === 'location' ? (linkedLead?.location || deal.location || '') :
                    field === 'projectType' ? (linkedLead?.projectType || deal.projectType || '') :
                      (linkedLead?.timeline || deal.timeline || '')
      }));
    }
  }, [deal, linkedLead, updateEnhancedDeal, updateLead]);

  const currentStage = useMemo(() =>
    pipeline.stages.find(s => s.id === deal.stageId),
    [pipeline.stages, deal.stageId]
  );

  const daysInStage = useMemo(() => {
    // Handle missing or invalid currentStageEnteredAt
    let enteredAt: Date;

    if (deal.currentStageEnteredAt) {
      enteredAt = new Date(deal.currentStageEnteredAt);
    } else if (deal.createdAt) {
      enteredAt = new Date(deal.createdAt);
    } else {
      return 0;
    }

    // Check if date is valid and reasonable (not before year 2000)
    const minValidDate = new Date('2000-01-01').getTime();
    if (isNaN(enteredAt.getTime()) || enteredAt.getTime() < minValidDate) {
      if (deal.createdAt) {
        const createdDate = new Date(deal.createdAt);
        if (!isNaN(createdDate.getTime()) && createdDate.getTime() >= minValidDate) {
          enteredAt = createdDate;
        } else {
          return 0;
        }
      } else {
        return 0;
      }
    }

    const now = new Date();
    const days = Math.floor((now.getTime() - enteredAt.getTime()) / (1000 * 60 * 60 * 24));

    // Sanity check
    if (days < 0 || days > 3650) {
      return 0;
    }

    return days;
  }, [deal.currentStageEnteredAt, deal.createdAt]);

  const totalDaysInPipeline = useMemo(() => {
    const createdAt = new Date(deal.createdAt);
    const now = new Date();
    return Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
  }, [deal.createdAt]);

  const isRotting = useMemo(() => {
    if (!currentStage?.rottingDays) return false;
    return daysInStage > currentStage.rottingDays;
  }, [daysInStage, currentStage]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    // Detect sentinel/missing dates (MISSING_TIMESTAMP_SENTINEL = 2000-01-01 UTC)
    if (isNaN(d.getTime()) || d.getFullYear() < 2001) {
      return 'Date not recorded';
    }
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDuration = (ms: number): string => {
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Same day';
    if (days === 1) return '1 day';
    return `${days} days`;
  };


  const handleMarkWon = async () => {
    if (!deal.id || !window.confirm('Mark this deal as WON?')) return;
    setLoading(true);
    try {
      await markDealWon(deal.id, userId);
      // Show the project creation modal after marking as won
      setShowProjectModal(true);
    } catch (err) {
      console.error('Error marking deal as won:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkLost = async () => {
    if (!deal.id || !lostReason.trim()) return;
    setLoading(true);
    try {
      await markDealLost(deal.id, lostReason, userId);
      setShowLostModal(false);
      onClose();
    } catch (err) {
      console.error('Error marking deal as lost:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!deal.id || !window.confirm('Reactivate this deal? It will move back to the pipeline.')) return;
    setLoading(true);
    try {
      await markDealActive(deal.id, userId);
      onClose();
    } catch (err) {
      console.error('Error reactivating deal:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToProject = async (frameType?: FrameType) => {
    if (deal.outcome !== 'won') {
      alert('Only won deals can be converted to projects');
      return;
    }
    setLoading(true);
    try {
      const projectId = await convertDealToProject(deal, userId, frameType || selectedFrameType);
      setShowProjectModal(false);
      alert(`Project created successfully!`);
      onClose();
    } catch (err) {
      console.error('Error converting to project:', err);
      alert('Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deal.id || !window.confirm('Delete this deal? This cannot be undone.')) return;
    setLoading(true);
    try {
      await deleteEnhancedDeal(deal.id);
      onClose();
    } catch (err) {
      console.error('Error deleting deal:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ddp-overlay" onClick={onClose}>
      <div className="ddp-panel" onClick={e => e.stopPropagation()}>

        {/* ═══ Header ═══ */}
        <div className="ddp-header">
          <div className="ddp-header-left">
            <div className="ddp-badge" style={{ backgroundColor: currentSourceColor }}>
              {sourceLetter}
            </div>
            <div className="ddp-header-info">
              <h2
                className="ddp-title"
                onClick={() => setShowRenameWizard(true)}
                title="Click to rename project (Project Number stays the same)"
              >
                {displayProjectTitle}
              </h2>
              <span className="ddp-contact">{displayContactName}</span>
            </div>
          </div>
          <button className="ddp-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* ═══ Stats Strip ═══ */}
        <div className="ddp-stats">
          <div className="ddp-stat">
            <span className="ddp-stat-label">Stage</span>
            <span className="ddp-stat-value">
              {currentStage?.name}
              {isRotting && <span className="ddp-stalled"><AlertTriangle size={11} /> Stalled</span>}
            </span>
          </div>
          <div className="ddp-stat">
            <span className="ddp-stat-label">Value</span>
            {deal.outcome === 'won' ? (
              <span
                className="ddp-stat-value"
                title="Value is derived from the signed proposal + change orders — manage via change orders"
              >
                {formatCurrency(localValues.value)}
              </span>
            ) : editingField === 'value' ? (
              <input
                type="number"
                value={localValues.value}
                onChange={(e) => handleFieldUpdate('value', e.target.value)}
                onBlur={() => setEditingField(null)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                className="ddp-value-input"
                autoFocus
              />
            ) : (
              <span className="ddp-stat-value ddp-editable" onClick={() => setEditingField('value')} title="Click to edit">
                {formatCurrency(localValues.value)}
              </span>
            )}
          </div>
          <div className="ddp-stat">
            <span className="ddp-stat-label">Prob</span>
            <span className="ddp-stat-value">{deal.probability}%</span>
          </div>
          <div className="ddp-stat">
            <span className="ddp-stat-label">Days</span>
            <span className={`ddp-stat-value ${isRotting ? 'ddp-warn' : ''}`}>{daysInStage}</span>
          </div>
        </div>

        {/* ═══ Designer & external architect ═══ */}
        {deal.stageId === 'design' && (() => {
          const designersList = listDesigners(crmUsers);
          const architectsList = listAssignableArchitects(crmUsers, architectInvites);
          const designer = getAssignedDesigner(deal, crmUsers);
          const externalArchitect = getExternalArchitectName(deal, crmUsers);
          const architectUids = getAssignedArchitectUids(deal);

          return (
            <>
              <div className="ddp-architect-row">
                <span className="ddp-architect-label">Designer</span>
                <select
                  className="ddp-architect-select"
                  value={designer.uid}
                  onChange={async (e) => {
                    if (!deal.id) return;
                    const uid = e.target.value;
                    const name = designersList.find((d) => d.uid === uid)?.displayName || '';
                    const patch: UpdateData<DocumentData> = {
                      assignedDesigner: uid ? uid : deleteField(),
                      assignedDesignerName: name ? name : deleteField(),
                      updatedAt: Timestamp.now(),
                    };
                    if (uid) patch.assignedArchitect = deleteField();
                    await updateDoc(doc(db, 'enhancedDeals', deal.id), patch);
                  }}
                >
                  <option value="">— Unassigned —</option>
                  {designersList.map((d) => (
                    <option key={d.uid} value={d.uid}>{d.displayName}</option>
                  ))}
                </select>
              </div>
              <div className="ddp-architect-row">
                <span className="ddp-architect-label">Architect</span>
                <input
                  type="text"
                  className="ddp-architect-select ddp-architect-text"
                  defaultValue={externalArchitect}
                  key={`${deal.id}-arch-${externalArchitect}`}
                  placeholder="External firm…"
                  onBlur={async (e) => {
                    if (!deal.id) return;
                    const trimmed = e.target.value.trim();
                    if (trimmed === externalArchitect) return;
                    await updateDoc(doc(db, 'enhancedDeals', deal.id), {
                      assignedArchitectName: trimmed ? trimmed : deleteField(),
                      updatedAt: Timestamp.now(),
                    });
                  }}
                />
              </div>
              {architectsList.length > 0 && (
                <div className="ddp-architect-row">
                  <span className="ddp-architect-label">Portal access</span>
                  <select
                    className="ddp-architect-select"
                    value=""
                    aria-label="Add invited architect"
                    onChange={async (e) => {
                      if (!deal.id) return;
                      const uid = e.target.value;
                      if (!uid || architectUids.includes(uid)) return;
                      const next = [...architectUids, uid];
                      await updateDoc(doc(db, 'enhancedDeals', deal.id), {
                        assignedArchitectUids: next,
                        assignedArchitect: next[0],
                        updatedAt: Timestamp.now(),
                      });
                    }}
                  >
                    <option value="">Add invited architect…</option>
                    {architectsList
                      .filter((a) => !architectUids.includes(a.uid))
                      .map((a) => (
                        <option key={a.uid} value={a.uid}>{a.displayName}</option>
                      ))}
                  </select>
                </div>
              )}
              {architectUids.length > 0 && (
                <div className="ddp-architect-row">
                  <span className="ddp-architect-label" />
                  <div>
                    {architectUids.map((uid) => {
                      const label = architectsList.find((a) => a.uid === uid)?.displayName || uid;
                      return (
                        <button
                          key={uid}
                          type="button"
                          className="ddp-architect-select"
                          style={{ marginRight: 8, marginBottom: 4 }}
                          onClick={async () => {
                            if (!deal.id) return;
                            const next = architectUids.filter((id) => id !== uid);
                            await updateDoc(doc(db, 'enhancedDeals', deal.id), {
                              assignedArchitectUids: next,
                              assignedArchitect: next[0] ? next[0] : deleteField(),
                              updatedAt: Timestamp.now(),
                            });
                          }}
                        >
                          {label} ×
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ═══ Quick Actions Row ═══ */}
        <div className="ddp-actions">
          {deal.id && (
            <button className="ddp-proposal-btn" onClick={() => navigate(`/proposal-builder/${deal.id}`)}>
              <FileText size={14} />
              {hasExistingProposal ? 'Edit Proposal' : 'Build Proposal'}
            </button>
          )}
          {hasExistingProposal && (
            <button className="ddp-reset-btn" onClick={() => setShowResetProposalModal(true)} title="Reset Proposal">
              <RotateCcw size={14} />
            </button>
          )}
          {deal.leadId && (
            <button className="ddp-portal-btn" onClick={() => navigate(`/client/${deal.leadId}`)}>
              <LayoutDashboard size={14} />
              Client Portal
            </button>
          )}
          {deal.leadId && deal.id && (
            <button
              type="button"
              className="ddp-portal-btn"
              onClick={() => setShowTransferDealModal(true)}
              title="Move this project to a different lead"
            >
              <ArrowRightLeft size={14} />
              Transfer
            </button>
          )}
          {!deal.leadId && (
            <select
              className="ddp-link-lead-select"
              value=""
              onChange={async (e) => {
                const selectedLeadId = e.target.value;
                if (!selectedLeadId || !deal.id) return;
                try {
                  await linkDealToLead(deal.id, selectedLeadId);
                } catch (err) {
                  console.error('Failed to link deal to lead:', err);
                }
              }}
            >
              <option value="">Link to Lead...</option>
              {leads.filter(l => !l.archived).map(l => (
                <option key={l.id} value={l.id}>{l.firstName} {l.lastName}</option>
              ))}
            </select>
          )}
          {deal.id && (
            <button
              className={`ddp-sniper-toggle ${deal.sniperZone ? 'ddp-sniper-active' : ''}`}
              onClick={async () => {
                if (!deal.id) return;
                await updateEnhancedDeal(deal.id, { sniperZone: !deal.sniperZone } as any);
              }}
              title={deal.sniperZone ? 'Remove from Sniper Zone' : 'Add to Sniper Zone'}
            >
              <Crosshair size={15} />
            </button>
          )}
        </div>

        {/* ═══ Bid Summary ═══ */}
        {deal.id && getBidsForDeal(deal.id).length > 0 && (() => {
          const latestBid = getBidsForDeal(deal.id).sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )[0];
          const bidTotal = latestBid.groups.reduce((sum, g) => sum + g.total, 0);
          return (
            <div className="ddp-bid">
              <div className="ddp-bid-top">
                <span className="ddp-bid-label">{latestBid.bidNumber}</span>
                <span className={`ddp-bid-status s-${latestBid.status}`}>{latestBid.status}</span>
                <span className="ddp-bid-amount">{formatCurrency(bidTotal)}</span>
                <button className="ddp-bid-view" onClick={() => setSelectedBid(latestBid)}>View</button>
                <button className="ddp-bid-all" onClick={() => setShowBiddingModal(true)}>
                  All ({getBidsForDeal(deal.id).length})
                </button>
              </div>
            </div>
          );
        })()}

        {/* ═══ Scrollable Content ═══ */}
        <div className="ddp-content">

          {activeTab === 'details' && (
            <div className="ddp-details">
              {/* ── Deal Info Rows ── */}
              <div className="ddp-section-title">Deal Information</div>
              <div className="ddp-rows">
                <div className="ddp-row">
                  <span className="ddp-row-label">Pipeline</span>
                  <span className="ddp-row-value">{pipeline.name}</span>
                </div>
                <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'expectedCloseDate' && setEditingField('expectedCloseDate')}>
                  <span className="ddp-row-label">Expected Close</span>
                  {editingField === 'expectedCloseDate' ? (
                    <input type="date" value={localValues.expectedCloseDate}
                      onChange={(e) => handleFieldUpdate('expectedCloseDate', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                      className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()} />
                  ) : (
                    <span className="ddp-row-value ddp-clickable">
                      {localValues.expectedCloseDate ? formatDate(localValues.expectedCloseDate) : <em className="ddp-placeholder">Set date</em>}
                    </span>
                  )}
                </div>
                <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'createdAt' && setEditingField('createdAt')}>
                  <span className="ddp-row-label">Created</span>
                  {editingField === 'createdAt' ? (
                    <input type="date" value={localValues.createdAt}
                      onChange={(e) => handleFieldUpdate('createdAt', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                      className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()} />
                  ) : (
                    <span className="ddp-row-value ddp-clickable">
                      {formatDate(deal.createdAt)}
                    </span>
                  )}
                </div>
                <div className="ddp-row">
                  <span className="ddp-row-label">Total Days</span>
                  <span className="ddp-row-value">{totalDaysInPipeline}d</span>
                </div>
                <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'currentStageEnteredAt' && setEditingField('currentStageEnteredAt')}>
                  <span className="ddp-row-label">Stage Entered</span>
                  {editingField === 'currentStageEnteredAt' ? (
                    <input type="date" value={localValues.currentStageEnteredAt}
                      onChange={(e) => handleFieldUpdate('currentStageEnteredAt', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                      className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()} />
                  ) : (
                    <span className="ddp-row-value ddp-clickable">
                      {deal.currentStageEnteredAt ? formatDate(deal.currentStageEnteredAt) : 'Not set'}
                    </span>
                  )}
                </div>
                <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'projectType' && setEditingField('projectType')}>
                  <span className="ddp-row-label">Project Type</span>
                  {editingField === 'projectType' ? (
                    <input type="text" value={localValues.projectType}
                      onChange={(e) => handleFieldUpdate('projectType', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                      placeholder="Pavilion, Barn…" className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()} />
                  ) : (
                    <span className="ddp-row-value ddp-clickable">
                      {localValues.projectType || <em className="ddp-placeholder">Add type</em>}
                    </span>
                  )}
                </div>
                <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'company' && setEditingField('company')}>
                  <span className="ddp-row-label">Company</span>
                  {editingField === 'company' ? (
                    <input type="text" value={localValues.company}
                      onChange={(e) => handleFieldUpdate('company', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                      placeholder="Company name" className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()} />
                  ) : (
                    <span className="ddp-row-value ddp-clickable">
                      {localValues.company || <em className="ddp-placeholder">Add company</em>}
                    </span>
                  )}
                </div>
                {localValues.company.trim() ? (
                  <CompanyBillingAddressFields companyName={localValues.company} />
                ) : null}
                <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'location' && setEditingField('location')}>
                  <span className="ddp-row-label">Location</span>
                  {editingField === 'location' ? (
                    <input type="text" value={localValues.location}
                      onChange={(e) => handleFieldUpdate('location', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                      placeholder="City, State" className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()} />
                  ) : (
                    <span className="ddp-row-value ddp-clickable">
                      {localValues.location || <em className="ddp-placeholder">Add location</em>}
                    </span>
                  )}
                </div>
                <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'timeline' && setEditingField('timeline')}>
                  <span className="ddp-row-label">Timeline</span>
                  {editingField === 'timeline' ? (
                    <div className="ddp-timeline-edit" onClick={e => e.stopPropagation()}>
                      {(() => {
                        const parts = (localValues.timeline || '').split(' ');
                        const seasons = ['Spring', 'Summer', 'Fall', 'Winter'];
                        const currentSeason = seasons.includes(parts[0]) ? parts[0] : '';
                        const yearPart = parts[1];
                        const currentYear = yearPart && /^\d{4}$/.test(yearPart) ? yearPart : new Date().getFullYear().toString();
                        return (
                          <>
                            <select value={currentSeason} onChange={(e) => handleFieldUpdate('timeline', e.target.value ? `${e.target.value} ${currentYear}` : '')} className="ddp-select">
                              <option value="">Season</option>
                              <option value="Spring">Spring</option>
                              <option value="Summer">Summer</option>
                              <option value="Fall">Fall</option>
                              <option value="Winter">Winter</option>
                            </select>
                            <select value={currentYear} onChange={(e) => handleFieldUpdate('timeline', `${currentSeason || 'Spring'} ${e.target.value}`)} className="ddp-select">
                              {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() + i).map(y => (
                                <option key={y} value={y}>{y}</option>
                              ))}
                            </select>
                            <button className="ddp-done-btn" onClick={() => setEditingField(null)}>Done</button>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <span className={`ddp-row-value ddp-clickable ${localValues.timeline ? 'ddp-timeline-pill' : ''}`}>
                      {localValues.timeline || <em className="ddp-placeholder">Set timeline</em>}
                    </span>
                  )}
                </div>
                <div className="ddp-row">
                  <span className="ddp-row-label">Source</span>
                  <div className="ddp-source-row">
                    <span className="ddp-source-chip" style={{ backgroundColor: currentSourceColor }}>
                      {formatSourceName(localValues.source)}
                    </span>
                    <select value={localValues.source} onChange={(e) => handleFieldUpdate('source', e.target.value as LeadSource)} className="ddp-select ddp-source-sel">
                      <option value="unspecified">Unspecified</option>
                      <option value="web-form">Web Form</option>
                      <option value="facebook-form">Facebook Form</option>
                      <option value="instagram-form">Instagram Form</option>
                      <option value="ebook">Ebook</option>
                      <option value="instagram-follow">Instagram Follow</option>
                      <option value="facebook-follow">Facebook Follow</option>
                      <option value="b2b-visit">B2B Visit</option>
                      <option value="cold-call">Cold Call</option>
                      <option value="call-in">Call-in</option>
                      <option value="referral">Referral</option>
                    </select>
                  </div>
                </div>
                <div className="ddp-row">
                  <span className="ddp-row-label">Budget</span>
                  <span className="ddp-row-value">{formatCurrency(deal.budget)}</span>
                </div>
                {deal.stageId === 'design' && (
                  <div className="ddp-row ddp-row-edit" onClick={() => editingField !== 'designPaymentDate' && setEditingField('designPaymentDate')}>
                    <span className="ddp-row-label">Design Payment</span>
                    {editingField === 'designPaymentDate' ? (
                      <input type="date" value={localValues.designPaymentDate}
                        onChange={(e) => handleFieldUpdate('designPaymentDate', e.target.value)}
                        onBlur={() => setEditingField(null)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                        className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()} />
                    ) : (
                      <span className="ddp-row-value ddp-clickable">
                        {localValues.designPaymentDate ? formatDate(localValues.designPaymentDate) : <em className="ddp-placeholder">Set date</em>}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* ── Description ── */}
              <div className="ddp-section-title">Description</div>
              {editingField === 'description' ? (
                <textarea
                  value={localValues.description}
                  onChange={(e) => handleFieldUpdate('description', e.target.value)}
                  onBlur={() => setEditingField(null)}
                  placeholder="Add notes about this deal…"
                  rows={3}
                  autoFocus
                  className="ddp-textarea"
                />
              ) : (
                <p className="ddp-desc" onClick={() => setEditingField('description')} title="Click to edit">
                  {localValues.description || <em className="ddp-placeholder">Click to add description…</em>}
                </p>
              )}
            </div>
          )}

          {activeTab === 'intel' && linkedLead && (
            <div className="ddp-intel-tabs-wrapper">
              <div className="ddp-intel-subtabs">
                <button
                  className={`ddp-intel-subtab ${intelSubTab === 'enrich' ? 'active' : ''}`}
                  onClick={() => setIntelSubTab('enrich')}
                >
                  <Search size={14} />
                  Enrich
                </button>
                <button
                  className={`ddp-intel-subtab ${intelSubTab === 'insights' ? 'active' : ''}`}
                  onClick={() => setIntelSubTab('insights')}
                >
                  <Sparkles size={14} />
                  Insights
                </button>
              </div>
              {intelSubTab === 'enrich' && <LeadIntelligencePanel lead={linkedLead} />}
              {intelSubTab === 'insights' && <AIAdvisorPanel lead={linkedLead} />}
            </div>
          )}

          {activeTab === 'intel' && !linkedLead && (
            <div className="ddp-empty">No linked lead found for this deal.</div>
          )}

          {activeTab === 'activities' && deal.id && (
            <div className="ddp-activities">
              {deal.leadId && (
                <EngagementLettersList leadId={deal.leadId} lead={linkedLead} dealId={deal.id} compact={true} defaultExpanded={false} />
              )}
              <ActivityQuickLog entityType="deal" entityId={deal.id} entityName={deal.title} userId={userId} />
            </div>
          )}

          {activeTab === 'history' && (
            <div className="ddp-history">
              {deal.stageHistory.length === 0 ? (
                <p className="ddp-empty">No stage transitions recorded</p>
              ) : (
                <div className="ddp-timeline">
                  {[...deal.stageHistory]
                    .sort((a, b) => {
                      const aTime = new Date(b.transitionedAt).getTime();
                      const bTime = new Date(a.transitionedAt).getTime();
                      // Push sentinel dates (< 2001) to the end
                      const aValid = new Date(b.transitionedAt).getFullYear() >= 2001;
                      const bValid = new Date(a.transitionedAt).getFullYear() >= 2001;
                      if (aValid && !bValid) return -1;
                      if (!aValid && bValid) return 1;
                      return aTime - bTime;
                    })
                    .map((t, i) => {
                      const from = t.fromStage ? pipeline.stages.find(s => s.id === t.fromStage)?.name || t.fromStage : 'Lead';
                      const to = pipeline.stages.find(s => s.id === t.toStage)?.name || t.toStage;
                      return (
                        <div key={i} className="ddp-hist-item">
                          <div className="ddp-hist-dot" />
                          <div className="ddp-hist-body">
                            <span className="ddp-hist-trans">{from} → <strong>{to}</strong></span>
                            <span className="ddp-hist-date">{formatDate(t.transitionedAt)}{t.durationMs > 0 && t.durationMs < 3 * 365.25 * 24 * 60 * 60 * 1000 && ` · ${formatDuration(t.durationMs)}`}</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {proposalData?.signature && (
                <div className="ddp-signed">
                  <div className="ddp-signed-info">
                    <FileCheck size={20} />
                    <div>
                      <div className="ddp-signed-by">Signed: {proposalData.signature.signedBy}</div>
                      <div className="ddp-signed-date">
                        {(() => {
                          const s = proposalData.signature?.signedAt;
                          if (!s) return 'Date unknown';
                          const d = s instanceof Date ? s : (s as any).toDate?.() ? (s as any).toDate() : new Date((s as any).seconds * 1000);
                          return formatDate(d);
                        })()}
                      </div>
                    </div>
                  </div>
                  {proposalData.signedPdfUrl ? (
                    <a href={proposalData.signedPdfUrl} target="_blank" rel="noopener noreferrer" className="ddp-pdf-btn">
                      <Download size={14} /> PDF
                    </a>
                  ) : (
                    <button className="ddp-pdf-btn" onClick={handleDownloadSignedPdf} disabled={generatingSignedPdf}>
                      {generatingSignedPdf ? <><Loader2 size={14} className="ddp-spin" /> …</> : <><Download size={14} /> PDF</>}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ Footer ═══ */}
        <div className="ddp-footer">
          {deal.outcome === 'active' ? (
            <>
              <button className="ddp-won" onClick={handleMarkWon} disabled={loading}>✓ Won</button>
              <button className="ddp-lost" onClick={() => setShowLostModal(true)} disabled={loading}>✗ Lost</button>
              <span className="ddp-spacer" />
              <button className="ddp-del" onClick={handleDelete} disabled={loading} title="Delete"><Trash2 size={15} /></button>
            </>
          ) : deal.outcome === 'won' ? (
            <>
              <span className="ddp-outcome ddp-outcome-won">Deal Won</span>
              {editingField === 'closedAt' ? (
                <input type="date" value={localValues.closedAt}
                  onChange={(e) => handleFieldUpdate('closedAt', e.target.value)}
                  onBlur={() => setEditingField(null)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                  className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()}
                  style={{ maxWidth: 140, fontSize: 12 }} />
              ) : (
                <span className="ddp-outcome-date ddp-clickable" onClick={() => setEditingField('closedAt')} title="Click to edit closed date">
                  {deal.closedAt ? formatDate(deal.closedAt) : 'Set date'}
                </span>
              )}
              <span className="ddp-spacer" />
              <button className="ddp-convert" onClick={() => setShowProjectModal(true)} disabled={loading}>Create Project</button>
              <button className="ddp-del" onClick={handleDelete} disabled={loading} title="Delete"><Trash2 size={15} /></button>
            </>
          ) : (
            <>
              <span className="ddp-outcome ddp-outcome-lost">Deal Lost</span>
              {editingField === 'closedAt' ? (
                <input type="date" value={localValues.closedAt}
                  onChange={(e) => handleFieldUpdate('closedAt', e.target.value)}
                  onBlur={() => setEditingField(null)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingField(null); }}
                  className="ddp-inline-input" autoFocus onClick={e => e.stopPropagation()}
                  style={{ maxWidth: 140, fontSize: 12 }} />
              ) : (
                <span className="ddp-outcome-date ddp-clickable" onClick={() => setEditingField('closedAt')} title="Click to edit closed date">
                  {deal.closedAt ? formatDate(deal.closedAt) : 'Set date'}
                </span>
              )}
              {deal.lostReason && <span className="ddp-outcome-date">Reason: {deal.lostReason}</span>}
              <span className="ddp-spacer" />
              <button className="ddp-reactivate" onClick={handleReactivate} disabled={loading} title="Move deal back to pipeline">
                Reactivate
              </button>
              <button className="ddp-del" onClick={handleDelete} disabled={loading} title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        </div>

        {/* Lost Reason Modal */}
        {showLostModal && (
          <div className="lost-modal-overlay" onClick={() => setShowLostModal(false)}>
            <div className="lost-modal" onClick={e => e.stopPropagation()}>
              <h3>Mark Deal as Lost</h3>
              <p>Please provide a reason for losing this deal:</p>
              <textarea
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                placeholder="e.g., Went with competitor, Budget constraints, Project cancelled..."
                rows={3}
                autoFocus
              />
              <div className="modal-actions">
                <button onClick={() => setShowLostModal(false)}>Cancel</button>
                <button
                  className="confirm-lost"
                  onClick={handleMarkLost}
                  disabled={!lostReason.trim() || loading}
                >
                  {loading ? 'Saving...' : 'Mark as Lost'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Project Modal */}
        {showProjectModal && (
          <div className="project-modal-overlay" onClick={() => { setShowProjectModal(false); onClose(); }}>
            <div className="project-modal" onClick={e => e.stopPropagation()}>
              <h3><PartyPopper size={20} /> Congratulations! Deal Won!</h3>
              <p>Create a project to track production for <strong>{deal.title}</strong></p>

              <div className="frame-type-selection">
                <h4>Select Project Type</h4>
                <div className="frame-type-options">
                  <label className={`frame-option ${selectedFrameType === 'custom' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="frameType"
                      value="custom"
                      checked={selectedFrameType === 'custom'}
                      onChange={() => setSelectedFrameType('custom')}
                    />
                    <div className="frame-option-content">
                      <span className="frame-icon"><Home size={20} /></span>
                      <span className="frame-title">Custom Timber Frames</span>
                      <span className="frame-desc">Design → Production → Installation</span>
                    </div>
                  </label>
                  <label className={`frame-option ${selectedFrameType === 'kit' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="frameType"
                      value="kit"
                      checked={selectedFrameType === 'kit'}
                      onChange={() => setSelectedFrameType('kit')}
                    />
                    <div className="frame-option-content">
                      <span className="frame-icon"><Package size={20} /></span>
                      <span className="frame-title">Kit Frames</span>
                      <span className="frame-desc">Procurement → Production → Shipment → Delivery</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="project-preview">
                <h4>Project Details</h4>
                <div className="preview-row">
                  <span className="preview-label">Name:</span>
                  <span className="preview-value">{deal.title}</span>
                </div>
                <div className="preview-row">
                  <span className="preview-label">Client:</span>
                  <span className="preview-value">{displayContactName}</span>
                </div>
                <div className="preview-row">
                  <span className="preview-label">Value:</span>
                  <span className="preview-value">{formatCurrency(deal.value)}</span>
                </div>
                <div className="preview-row">
                  <span className="preview-label">Location:</span>
                  <span className="preview-value">{deal.location || '—'}</span>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="skip-btn"
                  onClick={() => { setShowProjectModal(false); onClose(); }}
                >
                  Skip for Now
                </button>
                <button
                  className="create-project-btn"
                  onClick={() => handleConvertToProject()}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : `Create ${selectedFrameType === 'kit' ? 'Kit' : 'Custom'} Project`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bidding Modal */}
        {showBiddingModal && deal.id && (
          <BiddingModal
            isOpen={true}
            onClose={() => setShowBiddingModal(false)}
            dealId={deal.id}
            contactId={deal.contactId}
            initialProjectName={deal.title}
            onSuccess={(bidId) => {
              setShowBiddingModal(false);
            }}
          />
        )}

        {/* Bid Details Panel */}
        {selectedBid && (
          <BidDetailsPanel
            bid={selectedBid}
            onClose={() => setSelectedBid(null)}
          />
        )}

        {/* Reset Proposal Confirmation Modal */}
        {showResetProposalModal && (
          <div className="reset-modal-overlay" onClick={() => setShowResetProposalModal(false)}>
            <div className="reset-modal" onClick={e => e.stopPropagation()}>
              <div className="reset-modal-header">
                <AlertTriangle size={24} className="warning-icon" />
                <h3>Reset Proposal Builder?</h3>
              </div>
              <div className="reset-modal-content">
                <p>
                  Are you sure you want to reset the proposal builder for <strong>{deal.title}</strong>?
                </p>
                <p className="warning-text">
                  This will permanently delete all proposal data including:
                </p>
                <ul>
                  <li>All node sets and their calculations</li>
                  <li>Design, production, materials, and install data</li>
                  <li>Profit margins and totals</li>
                  <li>Checklist progress</li>
                </ul>
                <p className="warning-text">
                  <strong>This action cannot be undone.</strong>
                </p>
              </div>
              <div className="reset-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowResetProposalModal(false)}
                  disabled={resettingProposal}
                >
                  Cancel
                </button>
                <button
                  className="confirm-reset-btn"
                  onClick={handleResetProposal}
                  disabled={resettingProposal}
                >
                  {resettingProposal ? 'Resetting...' : 'Yes, Reset Proposal'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Kit Selector Modal */}
        {showKitSelector && deal.id && (
          <KitSelectorModal
            isOpen={true}
            dealId={deal.id}
            dealTitle={deal.title}
            onClose={() => setShowKitSelector(false)}
            onKitAssigned={(kp) => {
              setKitProposal(kp);
              setShowKitSelector(false);
            }}
          />
        )}

        {/* Rename Project wizard — locks Project Number, edits ClientLabel + Descriptor.
            On confirm, fans out to every projection via the onEnhancedDealRenamed
            Cloud Function trigger. */}
        {showRenameWizard && (() => {
          const fallbackTitle = parseLegacyTitle(deal.title);
          const initialLabel =
            deal.clientLabel ||
            defaultClientLabelFromLead(linkedLead) ||
            fallbackTitle.label ||
            (deal.contactName?.split(' ').pop() ?? '');
          const initialDescriptor =
            deal.descriptor ||
            fallbackTitle.descriptor ||
            defaultDescriptorFromProjectType(deal.projectType) ||
            (deal.projectName ?? '');
          return (
            <NameProjectWizard
              mode="edit"
              deferProjectNumber={!deal.projectNumber}
              allowAssignProjectNumber={!deal.projectNumber}
              assignNumberHint={
                deal.outcome === 'won'
                  ? 'Under contract jobs need a project number for QuickBooks, files, and the client portal.'
                  : undefined
              }
              initialValue={{
                projectNumber: deal.projectNumber || '',
                clientLabel: initialLabel,
                descriptor: initialDescriptor,
              }}
              warningMessage="Rename will update Project P&L, file listings, calendar, and the client portal label. The Project Number, public proposal URL, and QuickBooks Job stay the same."
              onClose={() => setShowRenameWizard(false)}
              onConfirm={async (next) => {
                if (!deal.id) return;
                const trimmedNumber = next.projectNumber.trim();
                const newTitle = [trimmedNumber, next.clientLabel.trim(), next.descriptor.trim()].filter(Boolean).join(' ').trim();
                await updateEnhancedDeal(deal.id, {
                  ...(trimmedNumber ? { projectNumber: trimmedNumber } : {}),
                  clientLabel: next.clientLabel.trim(),
                  descriptor: next.descriptor.trim(),
                  title: newTitle,
                });
                setLocalValues(prev => ({ ...prev, title: newTitle }));
                setShowRenameWizard(false);
              }}
            />
          );
        })()}

      {showTransferDealModal && deal.id && (
        <TransferDealToLeadModal
          deal={deal}
          userId={userId}
          onClose={() => setShowTransferDealModal(false)}
          onSuccess={({ targetLeadId }) => {
            navigate(`/client/${targetLeadId}`);
          }}
        />
      )}
      </div>
    </div>
  );
};

export default DealDetailsPanel;
